import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, Send, User, Shield, Paperclip, Trash2, Download } from "lucide-react";

const STATUSES = ["open", "in_progress", "waiting_on_customer", "resolved", "closed"];
const STATUS_STYLE = {
  open: "pill-open",
  in_progress: "pill-review",
  waiting_on_customer: "pill-pending",
  resolved: "pill-approved",
  closed: "pill-closed",
};
const MAX_REPLY_FILE_MB = 25;

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [pending, setPending] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const canManageStatus = ["company_admin", "hr", "super_admin"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setT(data);
    } catch { toast.error("Failed to load ticket"); nav("/tickets"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!t) return <div className="p-16 text-center text-slate-500 font-mono text-sm">Loading ticket…</div>;

  const addFiles = (list) => {
    const files = Array.from(list || []);
    const bad = files.find((f) => f.size > MAX_REPLY_FILE_MB * 1024 * 1024);
    if (bad) return toast.error(`${bad.name} exceeds ${MAX_REPLY_FILE_MB} MB`);
    setPending((prev) => [...prev, ...files]);
  };
  const removeFile = (idx) => setPending((prev) => prev.filter((_, i) => i !== idx));

  const uploadPending = async () => {
    const uploaded = [];
    for (const f of pending) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "ticket_attachment");
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      uploaded.push({
        id: data.id,
        filename: data.filename,
        content_type: data.content_type,
        size: data.size,
        fingerprint: data.fingerprint,
      });
    }
    return uploaded;
  };

  const send = async () => {
    if (!reply.trim() && pending.length === 0) return;
    setSending(true);
    try {
      const attachments = pending.length ? await uploadPending() : [];
      const { data } = await api.post(`/tickets/${id}/reply`, {
        message: reply || (attachments.length ? "(attachment)" : ""),
        attachments,
      });
      setT(data);
      setReply("");
      setPending([]);
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, { status });
      setT(data);
      toast.success(`Status updated to ${status.replaceAll("_", " ")}`);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <Link to="/tickets" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
        <ArrowLeft size={12}/> ALL TICKETS
      </Link>
      <SectionHeader
        title={t.subject}
        subtitle={
          <span className="font-mono text-xs">
            {t.ticket_number} · {t.category} · priority <span className="uppercase">{t.priority}</span>
          </span>
        }
        right={
          <div className="flex items-center gap-2">
            <span className={"pill " + (STATUS_STYLE[t.status] || "pill-open")}>{t.status.replaceAll("_", " ")}</span>
            {canManageStatus && (
              <select value={t.status} onChange={(e) => setStatus(e.target.value)} className="input !w-auto !py-1 !text-xs" data-testid="ticket-status-select">
                {STATUSES.map(s => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
              </select>
            )}
          </div>
        }
      />

      <div className="space-y-3 mb-6">
        {t.messages.map((m, i) => {
          const isMine = m.author_id === user.id;
          const isSupport = ["super_admin"].includes(m.author_role);
          return (
            <motion.div
              key={m.id || i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={"flex gap-3 " + (isMine ? "flex-row-reverse" : "")}
            >
              <div className={"w-8 h-8 shrink-0 rounded-full flex items-center justify-center " +
                (isSupport ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-700")}>
                {isSupport ? <Shield size={14}/> : <User size={14}/>}
              </div>
              <div className={"max-w-[80%] rounded border p-3 " +
                (isMine ? "bg-blue-50 border-blue-200" : (isSupport ? "bg-slate-900 text-slate-100 border-slate-900" : "bg-white border-slate-200"))}>
                <div className="flex items-center gap-2 text-[10px] font-mono mb-1 opacity-80">
                  <span className="font-semibold uppercase tracking-wider">{m.author_name}</span>
                  <span>·</span>
                  <span className={"uppercase tracking-wider " + (isSupport ? "text-blue-300" : "")}>{m.author_role.replaceAll("_", " ")}</span>
                  <span>·</span>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</div>
                {(m.attachments || []).length > 0 && (
                  <div className={"mt-2 pt-2 border-t space-y-1 " + (isSupport ? "border-white/20" : "border-slate-200")} data-testid={`ticket-msg-attachments-${m.id || i}`}>
                    {(m.attachments || []).map((a) => (
                      <a
                        key={a.id}
                        href={`${api.defaults.baseURL}/files/${a.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={"flex items-center gap-2 text-[11px] font-mono hover:underline " + (isSupport ? "text-blue-200" : "text-blue-700")}
                        data-testid={`ticket-attachment-${a.id}`}
                      >
                        <Paperclip size={11} strokeWidth={2}/>
                        <span className="truncate">{a.filename}</span>
                        {a.size ? <span className={isSupport ? "text-slate-400" : "text-slate-500"}>{Math.round(a.size / 1024)} KB</span> : null}
                        <Download size={11} strokeWidth={2} className="ml-1"/>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {["resolved", "closed"].includes(t.status) ? (
        <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 text-center">
          This ticket is <span className="font-semibold">{t.status.replaceAll("_", " ")}</span>. Reopen by changing status.
        </div>
      ) : (
        <div className="data-cell p-4">
          <label className="eyebrow block mb-2">Reply</label>
          <textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply…"
            className="input mb-2"
            data-testid={TID.ticketReplyInput}
          />
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            data-testid="ticket-reply-file-input"
          />
          {pending.length > 0 && (
            <div className="mb-2 space-y-1" data-testid="ticket-reply-pending">
              {pending.map((f, idx) => (
                <div key={idx} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip size={11} strokeWidth={2} className="shrink-0 text-slate-500"/>
                    <span className="truncate">{f.name}</span>
                    <span className="text-slate-400 font-mono shrink-0">{Math.round(f.size / 1024)} KB</span>
                  </div>
                  <button onClick={() => removeFile(idx)} className="text-slate-500 hover:text-red-700" title="Remove">
                    <Trash2 size={12} strokeWidth={2}/>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => fileRef.current?.click()}
              data-testid="ticket-reply-attach"
            >
              <Paperclip size={12} strokeWidth={2}/> Attach files
            </button>
            <button className="btn-primary" onClick={send} disabled={sending} data-testid={TID.ticketReplySubmit}>
              <Send size={14}/> {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
